import { useEffect, useState, useSyncExternalStore } from "react";
import type { RefObject } from "react";

/** True while the element is on screen (with a margin), so loops can pause. */
export function useInView(ref: RefObject<Element | null>, margin = "200px"): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin]);
  return inView;
}

/**
 * Scroll progress of an element, 0..1, as a whole percent so it only
 * re-renders a hundred times. `through` measures travel while the element is
 * pinned (taller than the viewport); otherwise from entering to leaving.
 */
export function useProgress(ref: RefObject<Element | null>, through = false): number {
  const [percent, setPercent] = useState(0);
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = through ? -rect.top / Math.max(1, rect.height - vh) : (vh - rect.top) / (rect.height + vh);
      setPercent(Math.round(Math.min(1, Math.max(0, p)) * 100));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, through]);
  return percent;
}

/** Local wall clock, ticking once a second. */
export function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

export type Theme = "dark" | "light";
const THEME_KEY = "genie-studio-theme";
const watchers = new Set<() => void>();
let theme: Theme | null = null;

/** The stored choice; dark until the visitor picks light. Storage can be closed (private windows), then it stays dark. */
function stored(): Theme {
  if (theme) return theme;
  try {
    theme = window.localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark";
  } catch {
    theme = "dark";
  }
  return theme;
}

function flip() {
  theme = stored() === "dark" ? "light" : "dark";
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // the choice still holds for this visit
  }
  watchers.forEach(tell => tell());
}

function watch(tell: () => void) {
  watchers.add(tell);
  return () => {
    watchers.delete(tell);
  };
}

/** The studio pages' colour theme, shared by every page and kept between visits, and the switch for it. */
export function useTheme(): [Theme, () => void] {
  return [useSyncExternalStore(watch, stored, () => "dark"), flip];
}

export const pad = (n: number, len = 2) => String(n).padStart(len, "0");
export const clock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
export const stamp = (d: Date) => `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;

export const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Counts from zero to `target` once `run` turns true. */
export function useCountUp(target: number, run: boolean, ms = 1400): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (reducedMotion()) {
      setValue(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      setValue(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, ms]);
  return value;
}

/** True on a phone-width screen, where the layout drops to one column. */
export function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setNarrow(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return narrow;
}
